import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { ForgotPassword } from './forgotPassword';
import { Login } from './Login';
import { Register } from './Register';

type PageType = 'login' | 'register' | 'forgot-password' | 'verify-otp';

export function AuthPage() {
  const location = useLocation();

  const navigationState = location.state as { initialPage?: 'login' | 'register' } | undefined;

  const [activePage, setActivePage] = useState<PageType>('login');
  const [hideLogin, setHideLogin] = useState<boolean | null>(null);
  const [hideRegister, setHideRegister] = useState(false);
  const [open1, setOpen1] = useState('open-login');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const trapezoidRef = useRef<HTMLDivElement | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const type = searchParams.get('type');

    if (type === 'register') {
      if (isMobile) {
        setActivePage('register');
      } else {
        setOpen1('close-login');
      }
    } else {
      setActivePage('login');
    }
  }, [searchParams, isMobile]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (navigationState?.initialPage === 'register') {
      if (isMobile) {
        setActivePage('register');
      } else {
        setOpen1('close-login');
      }
    }
  }, [navigationState, isMobile]);

  const toggleAnimation = (newPage: PageType) => {
    if (isMobile) {
      setActivePage(newPage);
      return;
    }

    if (newPage === 'forgot-password') {
      setOpen1('close-login-open-forgot');
    } else if (activePage === 'login') {
      setOpen1('close-login');
    } else {
      setOpen1('close-register');
    }
  };

  const handleAnimationStart = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (isMobile) return;

    if (e.animationName === 'trapezoidLoginClose') {
      setHideLogin(true);
    }

    if (e.animationName === 'trapezoidLoginCloseOpenForgot') {
      setHideLogin(true);
    }

    if (e.animationName === 'trapezoidRegisterClose') {
      setHideRegister(true);
    }
  };

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (isMobile) return;

    if (e.animationName === 'trapezoidLoginClose') {
      setActivePage('register');
      setHideLogin(false);
    }

    if (e.animationName === 'trapezoidLoginCloseOpenForgot') {
      setActivePage('forgot-password');
      setHideLogin(false);
    }

    if (e.animationName === 'trapezoidRegisterClose') {
      setActivePage('login');
      setHideRegister(false);
    }

    if (e.animationName === 'trapezoidReveal') {
      setActivePage('login');
    }
  };

  return (
    <section
      style={{
        backgroundImage: "url('/images/authBg.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      className="w-full min-h-screen overflow-x-hidden relative"
    >
      <div
        className={`
    absolute z-20 transition-all duration-700
    top-6
    ${isMobile ? 'left-1/2 -translate-x-1/2' : activePage === 'login' ? 'right-6' : 'left-6'}
  `}
      >
        <img
          src="/images/logo.svg"
          alt="logo"
          className="w-[160px] sm:w-[180px] lg:w-[200px]"
          onClick={() => {
            navigate('/');
          }}
        />
      </div>

      <div
        ref={trapezoidRef}
        onAnimationStart={handleAnimationStart}
        onAnimationEnd={handleAnimationEnd}
        className={`
        bg-black flex items-center justify-center
        min-h-screen w-full
        ${!isMobile ? 'trapezoid lg:w-[77%]' : ''}
        ${!isMobile && activePage !== 'login' ? 'lg:justify-end' : ''}
      `}
        data-state={!isMobile ? open1 : undefined}
      >
        <div
          className={`
          w-full max-w-[420px]
          lg:w-1/2
          ${activePage === 'login' ? 'lg:pl-0' : 'lg:pr-24'}
          ${isMobile ? 'px-10' : 'pr-24'}
        `}
        >
          {activePage === 'login' && (
            <div className={`${!isMobile ? `login ${hideLogin ? 'login-hide' : ''}` : ''}`}>
              <Login toggleAnimation={toggleAnimation} />
            </div>
          )}
          {activePage === 'register' && (
            <div
              className={`${!isMobile ? `register ${hideRegister ? 'register-hide' : ''}` : ''}`}
            >
              <Register toggleAnimation={toggleAnimation} />
            </div>
          )}

          {activePage === 'forgot-password' && (
            <div>
              <ForgotPassword toggleAnimation={toggleAnimation} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
